import { t } from "@lingui/macro";
import { PlusIcon } from "@phosphor-icons/react";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@reactive-resume/ui";
import type { ReactNode } from "react";

/**
 * AddSectionCard — trailing "add a section" affordance in the editor (builder v2).
 *
 * Presentational. With no `options`, it's a single dashed button that calls
 * `onAdd` (e.g. create a custom section). With `options`, it opens a calm popover
 * menu of section types and calls `onSelectOption` with the chosen id.
 */
export type AddSectionOption = {
  id: string;
  label: string;
  /** Phosphor node, e.g. <TrophyIcon size={16} />. */
  icon?: ReactNode;
  description?: string;
};

export type AddSectionCardProps = {
  options?: AddSectionOption[];
  onAdd?: () => void;
  onSelectOption?: (id: string) => void;
};

const DashedButton = ({ onClick }: { onClick?: () => void }) => (
  <Button
    variant="ghost"
    className="h-auto w-full justify-center gap-2 rounded-2xl border border-dashed border-border py-4 text-sm font-medium text-muted-foreground hover:border-primary hover:bg-primary/5 hover:text-primary"
    aria-label={t`Add section`}
    onClick={onClick}
  >
    <PlusIcon size={18} />
    {t`Add section`}
  </Button>
);

export const AddSectionCard = ({ options, onAdd, onSelectOption }: AddSectionCardProps) => {
  if (!options || options.length === 0) {
    return <DashedButton onClick={onAdd} />;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div>
          <DashedButton />
        </div>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-72 p-1.5">
        <p className="px-2 py-1.5 font-mono text-[10px] text-muted-foreground">
          {t`Add a section`}
        </p>
        <div className="flex flex-col">
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              className="flex items-start gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-accent"
              onClick={() => onSelectOption?.(option.id)}
            >
              {option.icon ? <span className="mt-0.5 text-primary">{option.icon}</span> : null}
              <span className="min-w-0">
                <span className="block text-sm font-medium text-foreground">{option.label}</span>
                {option.description ? (
                  <span className="block text-xs text-muted-foreground">{option.description}</span>
                ) : null}
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
